/* eslint-disable jira/filename-case, jira/js/no-reduce-accumulator-spread */

/**
 * A tree-like registry of shortcuts, with a default, root registry.
 */

import type { ReactElement } from 'react';
import forEach from 'lodash/forEach';
import has from 'lodash/has';
import isNil from 'lodash/isNil';
import pull from 'lodash/pull';
import { isValidShortcut, isShortcutCollision } from './keys.tsx';

export type KeyMapItem = {
	callback: (e?: KeyboardEvent) => void;
	label?: ReactElement;
	priority?: number; // Added so we can deterministically resolve overrides,
	isNew?: boolean;
};
export type ActiveKeyMap = {
	[shortcut: string]: KeyMapItem;
};
export type KeyMap = {
	keyMap: ActiveKeyMap;
	label?: ReactElement | null;
	location?: string;
};
export type KeyMaps = KeyMap | KeyMap[];
const validateKeyMaps = (keyMaps: ActiveKeyMap[]) => {
	forEach(keyMaps, (keyMap: ActiveKeyMap, keyMapIndex: number) => {
		const shortcuts: string[] = Object.keys(keyMap);
		forEach(shortcuts, (shortcut: string, shortcutIndex: number) => {
			if (!isValidShortcut(shortcut)) {
				// eslint-disable-next-line no-console
				console.error(
					`"${shortcut}" is an incorrect shortcut. Please, update it according to a playbook entry on shortcuts.`,
				);
			}
			for (
				let indexForOtherKeymaps = keyMapIndex + 1;
				indexForOtherKeymaps < keyMaps.length;
				indexForOtherKeymaps += 1
			) {
				const anotherKeyMap: ActiveKeyMap = keyMaps[indexForOtherKeymaps];
				forEach(anotherKeyMap, (anotherAction, anotherShortcut: string) => {
					if (isShortcutCollision(anotherShortcut, shortcut)) {
						// If neither shortcut has a priority we have a conflict.. You should ideally
						// put a priority on one of these conflicting shortcut configs so you definitively
						// know which one should be active when there is a conflict.
						if (
							(isNil(keyMap[shortcut].priority) &&
								isNil(anotherKeyMap[anotherShortcut].priority)) ||
							keyMap[shortcut].priority === anotherKeyMap[anotherShortcut].priority
						) {
							// DO NOT LET THIS HAPPEN. ADD A PRIORITY!!!
							// eslint-disable-next-line no-console
							console.error(
								`"${shortcut}" has a collision with "${anotherShortcut}" shortcut from another keyMap.`,
							);
						}
					}
				});
			}
			for (
				let indexToCompareSelf = shortcutIndex + 1;
				indexToCompareSelf < shortcuts.length;
				indexToCompareSelf += 1
			) {
				if (isShortcutCollision(shortcut, shortcuts[indexToCompareSelf])) {
					// eslint-disable-next-line no-console
					console.error(
						`"${shortcut}" has a collision with "${shortcuts[indexToCompareSelf]}" shortcut from the same keyMap.`,
					);
				}
			}
		});
	});
};
const mergeKeyMaps = (keyMapsToMerge: ActiveKeyMap[]) =>
	// Instead of doing the standard Object.assign({}, ...keyMapsToMerge)
	// we want to have a bit more control over what we can and can't override
	// through the use of the priority
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	keyMapsToMerge.reduce<Record<string, any>>(
		(prev, keyMap) => ({
			...prev,
			// Add the keys that can be overridden
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			...Object.keys(keyMap).reduce<Record<string, any>>((newAccumulator, newKey) => {
				if (
					!has(prev, newKey) ||
					(prev[newKey].priority ?? 0) < (keyMap[newKey].priority ?? 1) // Default behaviour is override
				) {
					return {
						...newAccumulator,
						[newKey]: keyMap[newKey],
					};
				}
				return newAccumulator;
			}, {}),
		}),
		{},
	);
type KeyMapRetriever = () => KeyMap;
/**
 * Manages a hierarchical structure of keyboard shortcuts for an application. This class facilitates
 * the dynamic registration and deregistration of shortcut collections and individual shortcuts,
 * enabling a modular and flexible approach to shortcut management. It supports the aggregation
 * of shortcuts from various sources and the retrieval of current active shortcuts, catering to
 * complex application structures with nested components or features.
 */
export class ShortcutRegistry {
	// go/jfe-eslint
	children: ShortcutRegistry[] = [];

	keyMapRetrievers: KeyMapRetriever[] = [];

	addChildRegistry(child: ShortcutRegistry) {
		this.children.push(child);
		return (): void => {
			pull(this.children, child);
		};
	}

	addKeyMap(getKeyMap: KeyMapRetriever) {
		this.keyMapRetrievers.push(getKeyMap);
		if (process.env.NODE_ENV !== 'production') {
			const keyMaps = this.keyMapRetrievers.map((keyMapRetriever) => keyMapRetriever().keyMap);
			validateKeyMaps(keyMaps);
		}
		return (): void => {
			pull(this.keyMapRetrievers, getKeyMap);
		};
	}

	getActiveKeyMap(): ActiveKeyMap {
		const keyMaps: ActiveKeyMap[] = this.children.length
			? this.children.map((child) => child.getActiveKeyMap())
			: this.keyMapRetrievers.map((getKeyMap) => getKeyMap().keyMap);
		if (process.env.NODE_ENV !== 'production') {
			if (this === rootRegistry) {
				validateKeyMaps(keyMaps);
			}
		}
		return {
			...mergeKeyMaps(keyMaps),
		};
	}

	getKeyMapInfo(): KeyMap[] {
		const info: (KeyMap | KeyMap[])[] = this.children.length
			? this.children.map((child) => child.getKeyMapInfo())
			: this.keyMapRetrievers.map((getInfo) => getInfo());
		// @ts-expect-error - TS2769 - No overload matches this call.
		const flattenedInfo: KeyMap[] = [].concat(...info);
		return flattenedInfo;
	}
}
export const rootRegistry: ShortcutRegistry = new ShortcutRegistry();
